import React, { useCallback, useMemo, useState } from 'react';

import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import type { TaskEmployeeDistributionCardProps } from 'types/props';

import ConfirmationDialog from 'ui/shared/ConfirmationDialog';

import AutoEmployeeAssignmentDialog from './AutoEmployeeAssignmentDialog';
import {
  DistributionMode,
  getSingleDayApiDate,
  getTomorrowDateFilter,
  persistDistributionMode,
  readDistributionMode
} from './distributionMode';
import { useAutoEmployeeDistribution } from './hooks/useAutoEmployeeDistribution';
import { useDeptEmployeesCache } from './hooks/useDeptEmployeesCache';
import { useDistributionDeptSelection } from './hooks/useDistributionDeptSelection';
import { useDistributionTableState } from './hooks/useDistributionTableState';
import { useDistributionTasks } from './hooks/useDistributionTasks';
import { useManualEmployeeAssignment } from './hooks/useManualEmployeeAssignment';
import { DistributionFiltersHeader } from './parts/DistributionFiltersHeader';
import { DistributionFooterActions } from './parts/DistributionFooterActions';
import { DistributionSingleDayPicker } from './parts/DistributionSingleDayPicker';
import { DistributionTasksTable } from './parts/DistributionTasksTable';
import {
  DistributionRowContext,
  DistributionRowContextValue
} from './rowContext';

const TaskEmployeeDistributionCard: React.FC<
  TaskEmployeeDistributionCardProps
> = ({
  selectedDepartmentsFilter,
  treeDropDownValue,
  isDepartmentsLoaded,
  visible,
  toastRef,
  onClose,
  defaultDateFilter,
  variant = 'tasks',
  onAfterAssign
}) => {
  const isCalendarAssignmentsVariant = variant === 'calendarAssignments';
  const [distributionMode, setDistributionMode] = useState<DistributionMode>(
    () => readDistributionMode(variant)
  );
  const travelTimeOptimization = distributionMode === 'travelTime';

  const dept = useDistributionDeptSelection({
    visible,
    defaultSelection: selectedDepartmentsFilter,
    treeValue: treeDropDownValue
  });

  const tableState = useDistributionTableState({
    visible,
    defaultDateFilter,
    travelTimeOptimization
  });
  const { setDateFilter } = tableState;

  const { tasks, tasksLoading, reloadTasks } = useDistributionTasks({
    visible,
    hasDeptSelection: dept.selection.length > 0,
    deptValueForApi: dept.deptValueForApi,
    dateFilter: tableState.dateFilter,
    multiSortMeta: tableState.multiSortMeta,
    filters: tableState.filters,
    travelTimeOptimization
  });

  const cache = useDeptEmployeesCache(visible);

  const reloadAndNotify = async () => {
    await reloadTasks();
    await onAfterAssign?.();
  };

  const manual = useManualEmployeeAssignment({
    visible,
    toastRef,
    reloadTasks: isCalendarAssignmentsVariant ? reloadAndNotify : reloadTasks
  });

  const travelTimeDepartmentId = useMemo(() => {
    if (!travelTimeOptimization || dept.selection.length !== 1)
      return undefined;
    const key = dept.selection[0]?.key;
    const parsed = Number(key);
    return key !== undefined && key !== '' && !isNaN(parsed)
      ? parsed
      : undefined;
  }, [travelTimeOptimization, dept.selection]);

  const travelTimeDate = useMemo(
    () =>
      travelTimeOptimization
        ? getSingleDayApiDate(tableState.dateFilter)
        : undefined,
    [travelTimeOptimization, tableState.dateFilter]
  );

  const auto = useAutoEmployeeDistribution({
    visible,
    tasks,
    toastRef,
    travelTimeOptimization,
    departmentId: travelTimeDepartmentId,
    date: travelTimeDate
  });
  const { clearSelection, distributeSelected } = auto;

  const {
    assignEmployeeConfirmOpen,
    assignEmployeeConfirmMessage,
    dismissAssignEmployeeConfirm,
    confirmAssignEmployee
  } = manual;

  const rowCtx: DistributionRowContextValue = useMemo(
    () => ({
      employeesByKey: cache.employeesByKey,
      inFlight: cache.inFlight,
      errorsByKey: cache.errorsByKey,
      ensureEmployees: cache.ensureEmployees,
      showAllByTaskId: cache.showAllByTaskId,
      setShowAllForTask: cache.setShowAllForTask,
      manualExecutorsByTaskId: manual.manualExecutorsByTaskId,
      handleManualExecutorsChange: manual.handleManualExecutorsChange,
      onManualAssign: manual.onManualAssign
    }),
    [
      cache.employeesByKey,
      cache.inFlight,
      cache.errorsByKey,
      cache.ensureEmployees,
      cache.showAllByTaskId,
      cache.setShowAllForTask,
      manual.manualExecutorsByTaskId,
      manual.handleManualExecutorsChange,
      manual.onManualAssign
    ]
  );

  const handleOpenHistory = useCallback(() => {
    const params = new URLSearchParams();
    const departmentIds = dept.selection
      .map((department) => String(department.key))
      .filter(Boolean)
      .join(',');

    if (departmentIds) {
      params.set('departmentIds', departmentIds);
    }

    const search = params.toString();
    window.open(
      `/personAssignmentHistory${search ? `?${search}` : ''}`,
      '_blank'
    );
  }, [dept.selection]);

  const handleToggleDistributionMode = useCallback(() => {
    const nextMode: DistributionMode = travelTimeOptimization
      ? 'all'
      : 'travelTime';

    if (nextMode === 'travelTime') {
      setDateFilter(getTomorrowDateFilter());
    } else if (isCalendarAssignmentsVariant) {
      setDateFilter({ ...defaultDateFilter });
    }

    clearSelection();
    setDistributionMode(nextMode);
    persistDistributionMode(variant, nextMode);
  }, [
    clearSelection,
    defaultDateFilter,
    isCalendarAssignmentsVariant,
    setDateFilter,
    travelTimeOptimization,
    variant
  ]);

  return (
    <div className="flex flex-column gap-3">
      <ConfirmationDialog
        visible={assignEmployeeConfirmOpen}
        onHide={dismissAssignEmployeeConfirm}
        onCancel={dismissAssignEmployeeConfirm}
        onContinue={confirmAssignEmployee}
        header="Предупреждение по составу исполнителей"
        confirmLabel="Назначить"
        cancelLabel="Скорректировать состав исполнителей"
        message={assignEmployeeConfirmMessage}
      />
      <AutoEmployeeAssignmentDialog
        isTravelTimeOptimization={travelTimeOptimization}
        visible={auto.resultDialog.visible}
        setVisible={auto.resultDialog.setVisible}
        tasksToAssignResponse={auto.resultDialog.results}
        routes={auto.resultDialog.routes}
        failedTasks={auto.resultDialog.failedTasks}
        onApply={auto.applyAssignment}
        onApplied={async () => {
          clearSelection();
          if (isCalendarAssignmentsVariant) {
            await reloadAndNotify();
          } else {
            await reloadTasks();
          }
        }}
      />

      <div className="flex align-items-center gap-2">
        <span>
          {travelTimeOptimization
            ? 'Отфильтрованы наряды, доступные для распределения с учетом времени доезда. Показать все наряды, доступные для автоматического распределения без учета оптимизации доезда?'
            : 'Данный список нарядов распределяется без учета оптимизации времени доезда. Показать наряды, распределяемые с учетом времени доезда?'}
        </span>
        <Button
          type="button"
          label="Да"
          size="small"
          onClick={handleToggleDistributionMode}
        />
      </div>

      {!isCalendarAssignmentsVariant && (
        <DistributionFiltersHeader
          isDepartmentsLoaded={isDepartmentsLoaded}
          treeDropDownValue={treeDropDownValue}
          selection={dept.selection}
          onSelectionChange={dept.handleSelectionChange}
          dateFilter={tableState.dateFilter}
          onDateFilterChange={tableState.onDateFilterChange}
          onSingleDayChange={tableState.onSingleDayChange}
          travelTimeOptimization={travelTimeOptimization}
        />
      )}

      {isCalendarAssignmentsVariant && travelTimeOptimization && (
        <div className="flex">
          <DistributionSingleDayPicker
            dateFilter={tableState.dateFilter}
            onChange={tableState.onSingleDayChange}
          />
        </div>
      )}

      {dept.selection.length === 0 ? (
        <div className="text-500 py-4">
          Выберите участок, чтобы отобразить наряды
        </div>
      ) : tasksLoading ? (
        <div className="flex justify-content-center py-6">
          <ProgressSpinner />
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          <DistributionRowContext.Provider value={rowCtx}>
            <DistributionTasksTable
              tasks={tasks}
              multiSortMeta={tableState.multiSortMeta}
              onSortChange={tableState.onSortChange}
              filters={tableState.filters}
              onFiltersChange={tableState.onFiltersChange}
              selectedAuto={auto.autoByTaskId}
              onAutoSelectionChange={auto.setSelection}
              tableControlsEnabled={!isCalendarAssignmentsVariant}
            />
          </DistributionRowContext.Provider>

          {auto.isDistributing && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                zIndex: 1000,
                borderRadius: 'inherit'
              }}
            >
              <ProgressSpinner />
            </div>
          )}
        </div>
      )}

      <DistributionFooterActions
        hasTasks={tasks.length > 0}
        hasAnyAutoSelected={auto.hasAnySelected}
        isDistributing={auto.isDistributing}
        onSelectAll={auto.toggleSelectAll}
        onDistributeSelected={distributeSelected}
        onOpenHistory={handleOpenHistory}
        onClose={onClose}
      />
    </div>
  );
};

export default TaskEmployeeDistributionCard;
